import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLanguage } from '@/contexts/LanguageContext';

const ACCENT = '#C87533';
const LEFT_INF  = [38, 68, 98, 128, 158];
const RIGHT_INF = [322, 352, 382, 412, 442];

const POINTS_SL = [
  {
    key: 'hierarchy',
    title: 'HIERARHIJA EKIPE',
    points: [
      { label: 'Projektni vodja (Control Tower)', desc: 'SPOC za naročnika, strategija in polna KPI odgovornost.' },
      { label: '2 regionalna supervizorja',        desc: '80 % časa na terenu, coaching in real-time optimizacija poti.' },
      { label: '10 informatorjev (5 parov)',        desc: 'Doseganje DCC ciljev v HoReCa in natančen iPad vnos podatkov.' },
    ],
  },
  {
    key: 'quality',
    title: 'MEHANIZMI KAKOVOSTI',
    points: [
      { label: 'Mock sessions',    desc: 'Tedenske vaje in role-play za piljenje komunikacije.' },
      { label: 'Best practices',   desc: 'Redni sestanki za prenos zmagovalnih pristopov med ekipami.' },
      { label: 'Poročanje v živo', desc: 'Takojšen prenos DCC podatkov v centralno nadzorno ploščo.' },
    ],
  },
];

const POINTS_EN = [
  {
    key: 'hierarchy',
    title: 'TEAM HIERARCHY',
    points: [
      { label: 'Project Manager (Control Tower)', desc: 'SPOC for client, strategy and full KPI accountability.' },
      { label: '2 Regional Supervisors',           desc: '80% in the field, coaching and real-time route optimisation.' },
      { label: '10 Informers (5 pairs)',            desc: 'Achieving DCC targets in HoReCa with precise iPad data entry.' },
    ],
  },
  {
    key: 'quality',
    title: 'QUALITY MECHANISMS',
    points: [
      { label: 'Mock sessions',    desc: 'Weekly practice and role-play sessions for communication refinement.' },
      { label: 'Best practices',   desc: 'Regular meetings to transfer winning approaches across teams.' },
      { label: 'Live reporting',   desc: 'Instant transfer of DCC data to the central dashboard.' },
    ],
  },
];

const OrgChartSVG = ({ lang }) => (
  <svg viewBox="0 0 480 340" className="w-full h-auto">
    <defs>
      <marker id="orgArr" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto">
        <polygon points="0 0, 5 2.5, 0 5" fill={ACCENT} opacity="0.65" />
      </marker>
    </defs>

    {/* ── PM Box ── */}
    <rect className="org-node" x="160" y="8" width="160" height="40" rx="8"
      fill={`${ACCENT}1A`} stroke={ACCENT} strokeWidth="1.5" />
    <text x="240" y="24" textAnchor="middle" fill={ACCENT} fontSize="9.5" fontFamily="monospace" fontWeight="bold">
      {lang === 'sl' ? 'PROJEKTNI VODJA' : 'PROJECT MANAGER'}
    </text>
    <text x="240" y="38" textAnchor="middle" fill={ACCENT} fontSize="8" fontFamily="monospace" opacity="0.55">
      SPOC · KPI OWNER
    </text>

    {/* PM connector lines */}
    <line className="org-line" x1="240" y1="48" x2="240" y2="74" stroke={ACCENT} strokeWidth="1.5" strokeOpacity="0.5" />
    <line className="org-line" x1="98"  y1="74" x2="382" y2="74" stroke={ACCENT} strokeWidth="1.5" strokeOpacity="0.5" />
    <line className="org-line" x1="98"  y1="74" x2="98"  y2="106" stroke={ACCENT} strokeWidth="1.5" strokeOpacity="0.5" />
    <line className="org-line" x1="382" y1="74" x2="382" y2="106" stroke={ACCENT} strokeWidth="1.5" strokeOpacity="0.5" />

    {/* Supervisor West */}
    <rect className="org-node" x="18" y="106" width="160" height="38" rx="7"
      fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
    <text x="98" y="121" textAnchor="middle" fill="white" fontSize="9" fontFamily="monospace" fontWeight="bold" opacity="0.85">
      {lang === 'sl' ? 'SUPERVIZOR' : 'SUPERVISOR'}
    </text>
    <text x="98" y="135" textAnchor="middle" fill="white" fontSize="7.5" fontFamily="monospace" opacity="0.4">
      {lang === 'sl' ? 'CENTER-ZAHOD' : 'CENTRE-WEST'}
    </text>

    {/* Supervisor East */}
    <rect className="org-node" x="302" y="106" width="160" height="38" rx="7"
      fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
    <text x="382" y="121" textAnchor="middle" fill="white" fontSize="9" fontFamily="monospace" fontWeight="bold" opacity="0.85">
      {lang === 'sl' ? 'SUPERVIZOR' : 'SUPERVISOR'}
    </text>
    <text x="382" y="135" textAnchor="middle" fill="white" fontSize="7.5" fontFamily="monospace" opacity="0.4">
      {lang === 'sl' ? 'VZHOD' : 'EAST'}
    </text>

    {/* Supervisor → Informer lines — Left */}
    <line className="org-line" x1="98" y1="144" x2="98"  y2="163" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
    <line className="org-line" x1="38" y1="163" x2="158" y2="163" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
    {LEFT_INF.map((x, i) => (
      <line key={i} className="org-line" x1={x} y1="163" x2={x} y2="178" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
    ))}

    {/* Supervisor → Informer lines — Right */}
    <line className="org-line" x1="382" y1="144" x2="382" y2="163" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
    <line className="org-line" x1="322" y1="163" x2="442" y2="163" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
    {RIGHT_INF.map((x, i) => (
      <line key={i} className="org-line" x1={x} y1="163" x2={x} y2="178" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
    ))}

    {/* Informer circles — Left */}
    {LEFT_INF.map((x, i) => (
      <g key={i} className="org-inf">
        <circle cx={x} cy="192" r="13" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
        {/* Head */}
        <circle cx={x} cy="187" r="3.8" fill="rgba(255,255,255,0.6)" />
        {/* Shoulders / torso */}
        <path d={`M${x-7},203 Q${x-7},196 ${x},194.5 Q${x+7},196 ${x+7},203 Z`}
          fill="rgba(255,255,255,0.38)" />
      </g>
    ))}

    {/* Informer circles — Right */}
    {RIGHT_INF.map((x, i) => (
      <g key={i} className="org-inf">
        <circle cx={x} cy="192" r="13" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
        {/* Head */}
        <circle cx={x} cy="187" r="3.8" fill="rgba(255,255,255,0.6)" />
        {/* Shoulders / torso */}
        <path d={`M${x-7},203 Q${x-7},196 ${x},194.5 Q${x+7},196 ${x+7},203 Z`}
          fill="rgba(255,255,255,0.38)" />
      </g>
    ))}

    <text x="98"  y="217" textAnchor="middle" fill="white" fontSize="7.5" fontFamily="monospace" opacity="0.28">
      {lang === 'sl' ? '5 INFORMATORJEV' : '5 INFORMERS'}
    </text>
    <text x="382" y="217" textAnchor="middle" fill="white" fontSize="7.5" fontFamily="monospace" opacity="0.28">
      {lang === 'sl' ? '5 INFORMATORJEV' : '5 INFORMERS'}
    </text>

    {/* ── Improvement Cycle ── */}
    {/* Divider */}
    <line x1="20" y1="232" x2="460" y2="232" stroke="rgba(255,255,255,0.06)" strokeWidth="1" strokeDasharray="4 4" />

    {/* Bottom arc only — top is open so FIELDWORK text is unobstructed */}
    <path
      d="M 92,294 A 148,50 0 0 1 388,294"
      fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray="5 4" />

    {/* Section label */}
    <text x="240" y="248" textAnchor="middle" fill="white" fontSize="7" fontFamily="monospace"
      opacity="0.25" letterSpacing="4">
      {lang === 'sl' ? 'KROG IZBOLJŠAV' : 'IMPROVEMENT CYCLE'}
    </text>

    {/* ── Arrows — short diagonals top, short horizontal center ── */}
    {/* Training top-right → Fieldwork bottom-left */}
    <line className="org-flow" x1="132" y1="295" x2="170" y2="278"
      stroke={ACCENT} strokeWidth="1.5" strokeOpacity="0.7" markerEnd="url(#orgArr)" />
    {/* Fieldwork bottom-right → Reporting top-left */}
    <line className="org-flow" x1="310" y1="278" x2="348" y2="295"
      stroke={ACCENT} strokeWidth="1.5" strokeOpacity="0.7" markerEnd="url(#orgArr)" />
    {/* Reporting left-center → Training right-center (short horizontal) */}
    <line className="org-flow" x1="310" y1="310" x2="170" y2="310"
      stroke={ACCENT} strokeWidth="1.5" strokeOpacity="0.7" markerEnd="url(#orgArr)" />

    {/* ── TRAINING node ── */}
    <rect className="org-flow-node" x="46" y="295" width="124" height="30" rx="15"
      fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
    <text x="108" y="314" textAnchor="middle" fill="white" fontSize="8.5" fontFamily="monospace" opacity="0.7">
      {lang === 'sl' ? 'USPOSABLJANJE' : 'TRAINING'}
    </text>

    {/* ── FIELDWORK node (center, prominent) ── */}
    <rect className="org-flow-node" x="168" y="250" width="144" height="30" rx="15"
      fill={`${ACCENT}18`} stroke={`${ACCENT}55`} strokeWidth="1.2" />
    <text x="240" y="269" textAnchor="middle" fill={ACCENT} fontSize="9" fontFamily="monospace" fontWeight="bold">
      {lang === 'sl' ? 'TERENSKO DELO' : 'FIELDWORK'}
    </text>

    {/* ── REPORTING node ── */}
    <rect className="org-flow-node" x="310" y="295" width="124" height="30" rx="15"
      fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
    <text x="372" y="314" textAnchor="middle" fill="white" fontSize="8.5" fontFamily="monospace" opacity="0.7">
      {lang === 'sl' ? 'POROČANJE' : 'REPORTING'}
    </text>
  </svg>
);

export const OrgStructureSection = () => {
  const sectionRef = useRef(null);
  const { lang } = useLanguage();
  const POINTS = lang === 'sl' ? POINTS_SL : POINTS_EN;

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: { trigger: sectionRef.current, start: 'top 70%', once: true },
      });
      tl.fromTo('.orgst-header', { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6 })
        .fromTo('.org-node',     { opacity: 0, scale: 0.85, transformOrigin: 'center' }, { opacity: 1, scale: 1, duration: 0.4, stagger: 0.1 }, '-=0.2')
        .fromTo('.org-line',     { opacity: 0 }, { opacity: 1, duration: 0.5, stagger: 0.03 }, '-=0.3')
        .fromTo('.org-inf',      { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: 0.35, stagger: 0.05 }, '-=0.3')
        .fromTo('.org-flow-node',{ opacity: 0, scale: 0.9, transformOrigin: 'center' }, { opacity: 1, scale: 1, duration: 0.4, stagger: 0.1 }, '-=0.2')
        .fromTo('.org-flow',     { opacity: 0 }, { opacity: 1, duration: 0.5, stagger: 0.12 }, '-=0.3')
        .fromTo('.orgst-card',   { opacity: 0, x: -20 }, { opacity: 1, x: 0, duration: 0.5, stagger: 0.12 }, 0.1);
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="org-structure"
      className="scroll-section min-h-screen flex items-center px-4 py-20"
    >
      <div className="section-content max-w-[1100px] mx-auto w-full">

        {/* Header */}
        <div className="orgst-header text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-[#C87533]/30 bg-[#C87533]/8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C87533]" />
            <span className="font-mono text-xs text-[#C87533] uppercase tracking-widest">
              {lang === 'sl' ? 'Operativna struktura' : 'Operational Structure'}
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 leading-tight">
            {lang === 'sl'
              ? <>Operativna <span style={{ color: ACCENT }}>Struktura in Nadzor</span></>
              : <>Operational <span style={{ color: ACCENT }}>Structure & Control</span></>
            }
          </h2>
          <p className="text-white/40 max-w-lg mx-auto text-sm leading-relaxed">
            {lang === 'sl'
              ? 'Brezhibna izvedba skozi nenehen nadzor in izboljšave.'
              : 'Flawless execution through continuous oversight and improvement.'}
          </p>
        </div>

        {/* Two-column: text cards left, org chart right */}
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-8 lg:gap-12 items-center">

          {/* Left — text cards */}
          <div className="flex flex-col gap-5">
            {POINTS.map((block) => (
              <div
                key={block.key}
                className="orgst-card relative rounded-2xl border border-white/8 bg-white/3 backdrop-blur-sm p-6 flex flex-col gap-4"
              >
                <div
                  className="absolute top-0 left-6 right-6 h-px rounded-full"
                  style={{ background: `linear-gradient(90deg, ${ACCENT}70, transparent)` }}
                />
                <h3 className="font-mono text-xs font-bold tracking-widest uppercase" style={{ color: ACCENT }}>
                  {block.title}
                </h3>
                <div className="flex flex-col gap-3">
                  {block.points.map((pt, i) => (
                    <div key={i} className="flex gap-3">
                      <span className="w-1 h-1 rounded-full flex-shrink-0 mt-[7px]" style={{ background: ACCENT }} />
                      <div>
                        <span className="text-white/80 text-sm font-semibold">{pt.label}: </span>
                        <span className="text-white/45 text-sm leading-relaxed">{pt.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Right — animated org chart */}
          <div
            className="orgst-card relative rounded-2xl border border-white/8 bg-white/3 backdrop-blur-sm p-5"
          >
            <div
              className="absolute top-0 left-6 right-6 h-px rounded-full"
              style={{ background: `linear-gradient(90deg, ${ACCENT}70, transparent)` }}
            />
            <OrgChartSVG lang={lang} />
          </div>

        </div>

      </div>
    </section>
  );
};
